import { z } from 'zod';
import type { Action } from '@il-colpo/engine';

/**
 * The actions a client may send, as the server reads them. The engine decides whether an action is
 * *legal* (phase, role, puzzle state); this decides whether it is *well formed*: known type,
 * right fields, bounded sizes and numbers, nothing extra that could surprise code written for typed
 * input. `tick` is not here: time is the server's alone.
 */

const id = z.string().min(1).max(64);
const smallInt = z.number().int().min(-1_000_000).max(1_000_000);

const Dir8 = z.enum(['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']);
const Dir4 = z.enum(['n', 'e', 's', 'w']);

export const PuzzleActSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('lock.set'), pin: smallInt, height: smallInt }),
  z.strictObject({ kind: z.literal('safe.try'), digits: z.array(smallInt).max(12) }),
  z.strictObject({
    kind: z.literal('panel.act'),
    wire: smallInt,
    act: z.enum(['cut', 'connect']),
  }),
  z.strictObject({ kind: z.literal('cameras.rotate'), camera: smallInt, orientation: Dir8 }),
  z.strictObject({ kind: z.literal('cameras.verify') }),
  z.strictObject({ kind: z.literal('laser.flip'), mirror: smallInt }),
  z.strictObject({ kind: z.literal('laser.fire') }),
  z.strictObject({ kind: z.literal('password.try'), word: smallInt }),
  z.strictObject({ kind: z.literal('corridor.move'), dir: z.union([Dir4, z.literal('stay')]) }),
  z.strictObject({ kind: z.literal('corridor.coin'), x: smallInt, y: smallInt }),
  z.strictObject({ kind: z.literal('corridor.petard'), x: smallInt, y: smallInt }),
  z.strictObject({ kind: z.literal('escape.move'), block: smallInt, delta: smallInt }),
  z.strictObject({ kind: z.literal('vault.set'), pos: smallInt, symbol: smallInt }),
  z.strictObject({ kind: z.literal('vault.confirm') }),
  z.strictObject({ kind: z.literal('vault.unconfirm') }),
]);

const Gear = z.enum(['proPick', 'jammer', 'rope', 'disguise', 'drone', 'emp']);

export const PlanSchema = z.strictObject({
  entrance: z.enum(['roof', 'sewer', 'service']),
  route: z.array(id).max(24),
  detours: z.array(z.strictObject({ anchor: id, room: id })).max(12),
  gear: z.array(Gear).max(8),
});

const PhraseRefSchema = z.strictObject({
  id: z.string().min(1).max(48),
  params: z.array(z.union([smallInt, z.string().max(48)])).max(8),
});

const SharesSchema = z
  .record(id, z.number().int().min(0).max(1_000_000))
  .refine((shares) => Object.keys(shares).length <= 5, 'at most five shares');

export const ActionSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('ready') }),
  z.strictObject({ type: z.literal('plan.propose'), plan: PlanSchema }),
  z.strictObject({ type: z.literal('plan.vote'), approve: z.boolean() }),
  z.strictObject({ type: z.literal('plan.tiebreak'), decision: z.enum(['approve', 'revise']) }),
  z.strictObject({ type: z.literal('puzzle.act'), puzzleId: id, act: PuzzleActSchema }),
  z.strictObject({ type: z.literal('gear.use'), gear: Gear, puzzleId: id.optional() }),
  z.strictObject({ type: z.literal('power.use'), power: z.enum(['planB', 'loop']) }),
  z.strictObject({ type: z.literal('exec.skipOptional') }),
  z.strictObject({ type: z.literal('sabotage.use'), kind: z.enum(['tipoff', 'tamper', 'rush']) }),
  z.strictObject({ type: z.literal('chat.quick'), phrase: PhraseRefSchema }),
  z.strictObject({
    type: z.literal('ping'),
    kind: z.enum(['here', 'danger', 'wait', 'go']),
    room: id.nullable(),
  }),
  z.strictObject({ type: z.literal('pin.place'), target: id.nullable() }),
  z.strictObject({ type: z.literal('accuse.vote'), target: id }),
  z.strictObject({ type: z.literal('split.propose'), shares: SharesSchema }),
  z.strictObject({ type: z.literal('split.vote'), approve: z.boolean() }),
  z.strictObject({ type: z.literal('reveal.continue') }),
]);

/** What a client may send: an action of the engine, other than `tick`. */
export type WireAction = z.infer<typeof ActionSchema>;

// Compile-time check that the schema and the engine agree: a wire action is an engine action.
const assertWireIsAction = (a: WireAction): Exclude<Action, { type: 'tick' }> => a;
void assertWireIsAction;
